/**
 * Flipper Stack PDP — web component
 * Loaded deferred to avoid blocking the main thread.
 * Depends on window.NIP_DATA (flipper-nip-data.js, also deferred but listed first).
 */
if (!customElements.get('flipper-stack-pdp')) {
  class FlipperStackPDP extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-pdp-data]').textContent);
      this.variantInput = this.querySelector('[data-pdp-variant-input]');
      this.sellingPlanInput = this.querySelector('[data-pdp-selling-plan-input]');
      this.atcPrice = this.querySelector('[data-pdp-atc-price]');
      this.atcBtn = this.querySelector('.pdp-atc-btn');
      this.currentVariant = this.data.currentVariant;
      this.currentSellingPlanId = null;
      this.bindFrequencyToggle();
      this.bindNIPModal();
      this.populateNIPAccordion();
      this.bindOfferFlow();
    }

    /**
     * Hook into Impact's product-form rather than competing with it.
     *
     * Previous implementation (bindATCOverride) registered its own submit
     * listener on the form to do its own /cart/add.js call. But the form
     * has `is="product-form"` — Impact's ProductForm custom element
     * (theme.js ~L2804) registers its OWN submit listener at element-
     * upgrade time. Both listeners fired on every click, each making its
     * own /cart/add.js POST, resulting in TWO items added to cart.
     *
     * Fix: piggyback on Impact's own `variant:add` event (dispatched on
     * the form after its /cart/add.js succeeds). We:
     *   1. Set event.detail.blockCartDrawerOpening = true when we need to
     *      redirect to checkout (so the drawer doesn't briefly open
     *      before navigation).
     *   2. Apply the offer's discount code via /discount/<code>.
     *   3. Redirect to /checkout if atcAction === 'checkout'; otherwise
     *      let Impact's CartDrawer open naturally.
     *
     * IMPORTANT: this file (assets/flipper-stack-pdp.js) is the ACTIVE
     * implementation — sections/flipper-stack-pdp.liquid and
     * sections/gf-flipper-stack-pdp.liquid both reference it via
     * `<script src="…" defer>`. They also include the same class
     * definition inside `{%- if false -%}` for documentation, but that
     * inline code never renders. ANY behaviour change must land HERE.
     */
    bindOfferFlow() {
      const discountCode = this.data.discountCode;
      const atcAction = this.data.atcAction;

      if (!discountCode && atcAction !== 'checkout') return;

      this.addEventListener('variant:add', async (event) => {
        /* Belt-and-braces guard: bail if we're already processing a
           previous variant:add. If anything ever causes variant:add to
           fire twice for a single ATC click (browser quirk, an app
           dispatching its own copy), the offer flow won't run twice. */
        if (this._offerFlowProcessing) return;
        this._offerFlowProcessing = true;

        /* Block Impact's auto-open drawer when we're about to navigate
           away to checkout — synchronous mutation seen by CartDrawer's
           later-firing bubble listener on document. */
        if (atcAction === 'checkout' && event.detail) {
          event.detail.blockCartDrawerOpening = true;
        }

        /* Keep the spinner visible (and the button uninteractive) for
           the full duration of /discount + navigation. The global ATC
           spinner in layout/theme.liquid has a document-level listener
           on variant:add that clears aria-busy from the button — if we
           let the event reach it, the spinner disappears mid-flight and
           the shopper sees the button go back to "ADD TO CART" while the
           /discount fetch is still in flight (~0.5–2 s window). That
           window is enough time for a second click → second /cart/add →
           2 items in cart.

           stopPropagation here keeps the button locked under the spinner
           overlay until window.location.href actually triggers navigation.
           Only applied to the checkout flow; the drawer-open flow
           legitimately wants the spinner to clear so the shopper can
           interact with the freshly-opened drawer. */
        if (atcAction === 'checkout') {
          event.stopPropagation();
          /* Also re-apply aria-busy in case any earlier-running document
             listener already cleared it (defence-in-depth). */
          if (this.atcBtn) this.atcBtn.setAttribute('aria-busy', 'true');
        }

        try {
          if (atcAction === 'checkout' && this.hasAttribute('data-gf-waffle-lander')) {
            const checkoutPath = discountCode
              ? `/discount/${encodeURIComponent(discountCode)}?redirect=${encodeURIComponent('/checkout')}`
              : '/checkout';
            window.location.href = checkoutPath;
            return;
          }
          if (discountCode) {
            await fetch(`/discount/${encodeURIComponent(discountCode)}`, { credentials: 'same-origin' });
          }
          if (atcAction === 'checkout') {
            window.location.href = '/checkout';
          }
        } catch (err) {
          console.error('Offer post-ATC flow failed:', err);
          /* Last-resort: if we promised the user a checkout redirect, deliver it. */
          if (atcAction === 'checkout') window.location.href = '/checkout';
        } finally {
          /* Reset the guard after a brief window so a SECOND (intentional)
             ATC click later in the session can still run the flow.
             Skipped on the checkout path — the page is navigating away,
             nothing to reset. */
          if (atcAction !== 'checkout') {
            setTimeout(() => { this._offerFlowProcessing = false; }, 1000);
          }
        }
      });
    }

    getNIPForLabel(label, badge) {
      if (!label || !window.NIP_DATA) return null;

      /* If the item has a "Gluten Free" badge, try the GF variant first
         e.g. label "Buttermilk" + badge "Gluten Free" → try "Gluten Free Buttermilk" */
      if (badge && badge.toLowerCase().includes('gluten free')) {
        const gfLabel = 'Gluten Free ' + label;
        if (window.NIP_DATA[gfLabel]) return window.NIP_DATA[gfLabel];
        const gfLower = gfLabel.toLowerCase();
        for (const [key, val] of Object.entries(window.NIP_DATA)) {
          if (gfLower === key.toLowerCase()) return val;
        }
      }

      /* Exact match */
      if (window.NIP_DATA[label]) return window.NIP_DATA[label];
      const lower = label.toLowerCase();
      /* Case-insensitive exact match */
      for (const [key, val] of Object.entries(window.NIP_DATA)) {
        if (lower === key.toLowerCase()) return val;
      }
      /* Partial match — prefer longest key match to avoid 'Maple' matching before 'Maple Syrup' */
      let bestMatch = null;
      let bestLen = 0;
      for (const [key, val] of Object.entries(window.NIP_DATA)) {
        const keyLower = key.toLowerCase();
        if ((lower.includes(keyLower) || keyLower.includes(lower)) && key.length > bestLen) {
          bestMatch = val;
          bestLen = key.length;
        }
      }
      return bestMatch;
    }

    bindNIPModal() {
      const trigger = this.querySelector('[data-pdp-nip-trigger]');
      const overlay = this.querySelector('[data-pdp-nip-overlay]');
      if (!trigger || !overlay) return;

      const modal = overlay.querySelector('.pdp-nip-modal');
      const closeBtn = overlay.querySelector('[data-pdp-nip-close]');

      /* Hide tabs for items without NIP data (e.g. physical accessories) */
      let defaultLabel = '';
      let defaultBadge = '';
      overlay.querySelectorAll('[data-nip-tab]').forEach(tab => {
        const nip = this.getNIPForLabel(tab.dataset.nipTab, tab.dataset.nipBadge);
        if (!nip) {
          tab.style.display = 'none';
        } else if (!defaultLabel) {
          defaultLabel = tab.dataset.nipTab;
          defaultBadge = tab.dataset.nipBadge || '';
          tab.classList.add('is-active');
        }
      });

      trigger.addEventListener('click', () => {
        this.populateNIPForLabel(defaultLabel, defaultBadge);
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });

      const closeModal = () => {
        modal.style.transform = '';
        modal.classList.remove('is-dragging');
        overlay.style.opacity = '';
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
      };

      closeBtn?.addEventListener('click', closeModal);
      overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal(); });

      /* Tab switching */
      overlay.querySelectorAll('[data-nip-tab]').forEach(tab => {
        tab.addEventListener('click', () => {
          overlay.querySelectorAll('[data-nip-tab]').forEach(t => t.classList.remove('is-active'));
          tab.classList.add('is-active');
          this.populateNIPForLabel(tab.dataset.nipTab, tab.dataset.nipBadge);
        });
      });

      /* Swipe-to-dismiss (mobile bottom-sheet) */
      let startY = 0, currentY = 0, isDragging = false;
      modal.addEventListener('touchstart', (e) => {
        const touchY = e.touches[0].clientY;
        const modalRect = modal.getBoundingClientRect();
        if (modal.scrollTop > 0 && (touchY - modalRect.top) > 40) return;
        startY = touchY; isDragging = true; modal.classList.add('is-dragging');
      }, { passive: true });
      modal.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        const delta = currentY - startY;
        if (delta > 0) { modal.style.transform = `translateY(${delta}px)`; overlay.style.opacity = Math.max(0.2, 1 - delta / 400); }
      }, { passive: true });
      modal.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false; modal.classList.remove('is-dragging');
        if (currentY - startY > 100) closeModal();
        else { modal.style.transform = ''; overlay.style.opacity = ''; }
        startY = 0; currentY = 0;
      });
    }

    populateNIPForLabel(label, badge) {
      const nip = this.getNIPForLabel(label, badge);
      if (!nip) return;

      const flavourEl = this.querySelector('[data-nip-flavour-name]');
      const ingredientsEl = this.querySelector('[data-nip-ingredients]');
      const allergensEl = this.querySelector('[data-nip-allergens]');
      const tbody = this.querySelector('[data-nip-tbody]');
      const servingHeader = this.querySelector('[data-nip-serving-header]');

      /* Show full name including badge prefix (e.g. "Gluten Free Buttermilk") */
      const displayName = (badge && badge.toLowerCase().includes('gluten free'))
        ? 'Gluten Free ' + label
        : label;
      if (flavourEl) flavourEl.textContent = displayName;
      if (ingredientsEl) ingredientsEl.textContent = nip.ingredients;
      if (allergensEl) { allergensEl.textContent = nip.allergens; allergensEl.style.display = nip.allergens ? '' : 'none'; }
      if (servingHeader) servingHeader.textContent = `Per serving (${nip.servingSize})`;
      if (!tbody) return;

      let html = '';
      html += `<tr><th>Servings per package:</th><td>${nip.servings}</td></tr>`;
      html += `<tr><th>Serving size</th><td>${nip.servingSize}</td></tr>`;
      html += `<tr class="pdp-nip-table__calories"><th>Calories</th><td>${nip.calories}</td></tr>`;
      for (const row of nip.rows) {
        const isSub = row[0].startsWith('_');
        const name = isSub ? row[0].slice(1) : row[0];
        html += `<tr${isSub ? ' class="pdp-nip-table__subrow"' : ''}><th>${name} ${row[1]}</th><td>${row[2]}</td></tr>`;
      }
      tbody.innerHTML = html;
    }

    populateNIPAccordion() {
      const accWrap = this.querySelector('[data-pdp-nip-accordion]');
      if (!accWrap) return;

      /* Get all What's Inside labels from tabs */
      const tabs = this.querySelectorAll('[data-nip-tab]');
      if (tabs.length === 0) return;

      let fullHtml = '';
      tabs.forEach(tab => {
        const label = tab.dataset.nipTab;
        const badge = tab.dataset.nipBadge || '';
        const nip = this.getNIPForLabel(label, badge);
        if (!nip) return;

        const heading = (badge && badge.toLowerCase().includes('gluten free'))
          ? 'Gluten Free ' + label : label;
        fullHtml += `<div style="margin-bottom:24px;">`;
        fullHtml += `<h4 style="font-family:var(--heading-font-family);font-weight:700;font-size:16px;margin:0 0 8px;">${heading}</h4>`;
        fullHtml += `<p style="font-size:14px;line-height:1.55;margin:0 0 4px;"><strong>Ingredients:</strong> ${nip.ingredients}</p>`;
        if (nip.allergens) {
          fullHtml += `<p style="font-size:13px;color:#888;font-style:italic;margin:0 0 8px;">${nip.allergens}</p>`;
        }
        fullHtml += `<table class="pdp-nip-table">`;
        fullHtml += `<thead><tr><th>Per serving (${nip.servingSize})</th><th>% Daily Value*</th></tr></thead><tbody>`;
        fullHtml += `<tr><th>Servings per package:</th><td>${nip.servings}</td></tr>`;
        fullHtml += `<tr><th>Serving size</th><td>${nip.servingSize}</td></tr>`;
        fullHtml += `<tr class="pdp-nip-table__calories"><th>Calories</th><td>${nip.calories}</td></tr>`;
        for (const row of nip.rows) {
          const isSub = row[0].startsWith('_');
          const name = isSub ? row[0].slice(1) : row[0];
          fullHtml += `<tr${isSub ? ' class="pdp-nip-table__subrow"' : ''}><th>${name} ${row[1]}</th><td>${row[2]}</td></tr>`;
        }
        fullHtml += `</tbody></table></div>`;
      });

      fullHtml += `<p style="font-size:13px;color:#888;font-style:italic;">Halal friendly. Certification pending.</p>`;
      accWrap.innerHTML = fullHtml;
    }

    bindFrequencyToggle() {
      const subRadio = this.querySelector('[data-pdp-frequency-radio][value="subscription"]');
      const freqSelect = this.querySelector('[data-pdp-frequency-select]');

      this.querySelectorAll('[data-pdp-frequency-radio]').forEach(radio => {
        radio.addEventListener('change', () => {
          if (radio.value === 'subscription' && subRadio) {
            const planId = freqSelect ? freqSelect.value : subRadio.dataset.sellingPlanId;
            this.sellingPlanInput.value = planId;
            this.sellingPlanInput.disabled = false;
            this.currentSellingPlanId = parseInt(planId);
          } else {
            this.sellingPlanInput.value = '';
            this.sellingPlanInput.disabled = true;
            this.currentSellingPlanId = null;
          }
          this.updatePrices();
        });
      });

      if (freqSelect) {
        freqSelect.addEventListener('change', () => {
          if (subRadio && subRadio.checked) {
            const planId = freqSelect.value;
            this.sellingPlanInput.value = planId;
            this.currentSellingPlanId = parseInt(planId);
            subRadio.dataset.sellingPlanId = planId;
            this.updatePrices();
          }
        });
      }
    }

    updatePrices() {
      if (!this.currentVariant) return;
      const discountPct = this.data.otpDiscountPercent || 0;
      const discountMul = 1 - (discountPct / 100);
      const otp = Math.round(this.currentVariant.price * discountMul);

      const otpEl = this.querySelector('[data-pdp-onetime-price]');
      if (otpEl) otpEl.textContent = this.formatMoney(otp);

      /* Show original as compare-at when discount is active */
      const otpCompareEl = this.querySelector('[data-pdp-onetime-compare]');
      if (otpCompareEl) {
        if (discountPct > 0) {
          otpCompareEl.textContent = this.formatMoney(this.currentVariant.price);
          otpCompareEl.style.display = '';
        } else if (this.currentVariant.compareAtPrice > this.currentVariant.price) {
          otpCompareEl.textContent = this.formatMoney(this.currentVariant.compareAtPrice);
          otpCompareEl.style.display = '';
        } else {
          otpCompareEl.style.display = 'none';
        }
      }

      let activePrice = otp;
      if (this.currentSellingPlanId) {
        const alloc = this.currentVariant.sellingPlanAllocations.find(a => a.sellingPlanId === this.currentSellingPlanId);
        if (alloc) activePrice = Math.round(alloc.price * discountMul);
      }
      if (this.atcPrice) this.atcPrice.textContent = this.formatMoney(activePrice);

      /* Subscription prices */
      const activePlanId = this.currentSellingPlanId
        || parseInt(this.querySelector('[data-pdp-frequency-select]')?.value)
        || parseInt(this.querySelector('[data-pdp-frequency-radio][value="subscription"]')?.dataset.sellingPlanId);

      const alloc = this.currentVariant.sellingPlanAllocations.find(a => a.sellingPlanId === activePlanId)
        || this.currentVariant.sellingPlanAllocations[0];

      if (alloc) {
        const subPrice = Math.round(alloc.price * discountMul);
        const priceEl = this.querySelector('[data-pdp-sub-price]');
        const compareEl = this.querySelector('[data-pdp-sub-compare]');
        if (priceEl) priceEl.textContent = this.formatMoney(subPrice);
        if (compareEl) compareEl.textContent = this.formatMoney(alloc.compareAtPrice);

        const badgeEl = this.querySelector('[data-pdp-save-badge]');
        if (badgeEl && this.currentVariant.price > 0) {
          const pct = Math.round((1 - alloc.price / this.currentVariant.price) * 100);
          badgeEl.textContent = `save ${pct}%`;
        }
      }
    }

    formatMoney(cents) {
      if (!this._moneyFmt) {
        try {
          const lang = document.documentElement.lang || 'en-AU';
          this._moneyFmt = new Intl.NumberFormat(lang, { style: 'currency', currency: this.data.currency || 'AUD' });
        } catch (e) { this._moneyFmt = null; }
      }
      if (this._moneyFmt) return this._moneyFmt.format(cents / 100);
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('flipper-stack-pdp', FlipperStackPDP);
}
